import { createSelector } from 'reselect';
import { RootState } from './store';
import { Offer, Campaign, AffiliateNetwork } from './api-interfaces';

// Create unified selectors that maintain backward compatibility while ensuring platform fields

// Unified offer selector for backward compatibility
export const selectUnifiedOfferList = createSelector(
  [(state: RootState) => state.trackerData.entities.offer],
  (offerEntities): Offer[] => {
    // Return unified list for backward compatibility, but each offer should have platform field
    const allOffers: Offer[] = [];
    
    // Add Voluum offers with platform field
    if (offerEntities.voluum) {
      allOffers.push(...offerEntities.voluum.map(offer => ({ ...offer, platform: 'voluum' as const })));
    }
    
    // Add RedTrack offers with platform field
    if (offerEntities.redtrack) {
      allOffers.push(...offerEntities.redtrack.map(offer => ({ ...offer, platform: 'redtrack' as const })));
    }
    
    // Add unified offers (already have platform field)
    if (offerEntities.unified) {
      allOffers.push(...offerEntities.unified);
    }
    
    return allOffers;
  }
);

// Unified campaign selector for backward compatibility
export const selectUnifiedCampaignList = createSelector(
  [(state: RootState) => state.trackerData.entities.campaign],
  (campaignEntities): Campaign[] => {
    const allCampaigns: Campaign[] = [];
    
    // Add Voluum campaigns with platform field
    if (campaignEntities.voluum) {
      allCampaigns.push(...campaignEntities.voluum.map(campaign => ({ ...campaign, platform: 'voluum' as const })));
    }
    
    // Add RedTrack campaigns with platform field
    if (campaignEntities.redtrack) {
      allCampaigns.push(...campaignEntities.redtrack.map(campaign => ({ ...campaign, platform: 'redtrack' as const })));
    }
    
    // Add unified campaigns (already have platform field)
    if (campaignEntities.unified) {
      allCampaigns.push(...campaignEntities.unified);
    }
    
    return allCampaigns;
  }
);

// Unified affiliate network selector for backward compatibility
export const selectUnifiedAffiliateNetworkList = createSelector(
  [
    (state: RootState) => state.trackerData.entities['affiliate-network'],
    (state: RootState) => state.platform.legacyAffiliateNetworkList
  ],
  (networkEntities, legacyNetworks): AffiliateNetwork[] => {
    const allNetworks: AffiliateNetwork[] = [];
    
    // Check if new platform-separated structure has data
    if (networkEntities) {
      // Add Voluum networks with platform field
      if (networkEntities.voluum) {
        allNetworks.push(...networkEntities.voluum.map(network => ({ ...network, platform: 'voluum' as const })));
      }
      
      // Add RedTrack networks with platform field
      if (networkEntities.redtrack) {
        allNetworks.push(...networkEntities.redtrack.map(network => ({ ...network, platform: 'redtrack' as const })));
      }
      
      // Add unified networks (already have platform field)
      if (networkEntities.unified) {
        allNetworks.push(...networkEntities.unified);
      }
    }
    
    // Fallback to legacy networks if no platform-separated data available
    if (allNetworks.length === 0 && legacyNetworks && legacyNetworks.length > 0) {
      allNetworks.push(...legacyNetworks.map(network => ({
        ...network,
        // Assume Voluum for existing networks until backend provides platform info
        platform: network.platform || 'voluum' as const
      })));
    }
    
    return allNetworks;
  }
);
